import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from "react";
import classNames from "classnames";
import { noop, get, toInteger } from "lodash-es";
import { Button, Classes } from "@blueprintjs/core";
import { onEnterOrBlurHelper } from "../utils/handlerHelpers";
import { defaultPageSizes } from "./utils/queryParams";
import { getIdOrCodeOrIndex } from "./utils";
function PagingInput({ disabled, onBlur, defaultPage }) {
  const [page, setPage] = useState(defaultPage);
  const defaultValue = useRef(defaultPage);
  useEffect(() => {
    if (page !== defaultPage && defaultValue.current !== defaultPage) {
      setPage(defaultPage);
    }
    defaultValue.current = defaultPage;
  }, [page, defaultPage]);
  return /* @__PURE__ */ jsx(
    "input",
    {
      name: "page",
      style: {
        marginLeft: 5,
        width: page > 999 ? 65 : page > 99 ? 45 : 35,
        marginRight: 8
      },
      value: page,
      disabled,
      onChange: (e) => {
        setPage(e.target.value);
      },
      ...onEnterOrBlurHelper(onBlur),
      className: Classes.INPUT
    }
  );
}
const PagingTool = ({
  controlled_hasNextPage,
  controlled_onRefresh,
  controlled_page,
  controlled_setPage,
  controlled_setPageSize,
  controlled_total,
  disabled: _disabled,
  disableSetPageSize,
  entities,
  entityCount,
  hideSetPageSize,
  hideTotalPages,
  keepSelectionOnPageChange,
  onRefresh: _onRefresh,
  page: _page,
  pageSize,
  pagingDisabled,
  persistPageSize = noop,
  scrollToTop,
  setPage: _setPage,
  setPageSize: _setPageSize,
  setSelectedEntityIdMap
}) => {
  const page = controlled_page || _page;
  const total = controlled_total || entityCount;
  const disabled = _disabled || pagingDisabled;
  const onRefresh = controlled_onRefresh || _onRefresh;
  const setPage = controlled_setPage || _setPage;
  const setPageSize = controlled_setPageSize || _setPageSize;
  const onPageChange = useCallback(() => {
    const record = get(entities, "[0]");
    if (!keepSelectionOnPageChange && (!record || !getIdOrCodeOrIndex(record))) {
      setSelectedEntityIdMap({});
    }
    scrollToTop();
  }, [
    entities,
    keepSelectionOnPageChange,
    scrollToTop,
    setSelectedEntityIdMap
  ]);
  const [refetching, setRefetching] = useState(false);
  useEffect(() => {
    const additionalPageSize = window.frontEndConfig && window.frontEndConfig.additionalPageSize ? [toInteger(window.frontEndConfig.additionalPageSize)] : [];
    window.tgPageSizes = [...defaultPageSizes, ...additionalPageSize];
  }, []);
  const onRefreshHandler = async () => {
    setRefetching(true);
    await onRefresh();
    setRefetching(false);
  };
  const setPageHandler = (page2) => {
    setPage(page2);
    onPageChange(page2);
  };
  const setPageSizeHandler = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    persistPageSize(newPageSize);
  };
  const pageBackHandler = () => {
    setPageHandler(parseInt(page, 10) - 1);
  };
  const pageForwardHandler = () => {
    setPageHandler(parseInt(page, 10) + 1);
  };
  const pageInputBlurHandler = (e) => {
    const lastPage2 = Math.ceil(total / pageSize);
    const pageValue = parseInt(e.target.value, 10);
    const selectedPage = pageValue > lastPage2 ? lastPage2 : pageValue < 1 || isNaN(pageValue) ? 1 : pageValue;
    setPageHandler(selectedPage);
  };
  const pageStart = (page - 1) * pageSize + 1;
  if (pageStart < 0) throw new Error("We should never have page be <0");
  const backEnabled = page - 1 > 0;
  const forwardEnabled = page * pageSize < total;
  const lastPage = Math.ceil(total / pageSize);
  const options = [...window.tgPageSizes || defaultPageSizes];
  if (!options.includes(pageSize)) {
    options.push(pageSize);
  }
  return /* @__PURE__ */ jsxs("div", { className: "paging-toolbar-container", children: [
    onRefresh && /* @__PURE__ */ jsx(
      Button,
      {
        minimal: true,
        loading: refetching,
        icon: "refresh",
        disabled,
        onClick: onRefreshHandler
      }
    ),
    !hideSetPageSize && /* @__PURE__ */ jsx(
      "div",
      {
        title: "Set Page Size",
        className: classNames(Classes.SELECT, Classes.MINIMAL),
        children: /* @__PURE__ */ jsx(
          "select",
          {
            name: "page-size",
            className: "paging-page-size",
            onChange: setPageSizeHandler,
            disabled: disabled || disableSetPageSize,
            value: pageSize,
            children: [
              /* @__PURE__ */ jsx("option", { disabled: true, value: "fake", children: "Size" }, "page-size-placeholder"),
              ...options.map((size) => {
                return /* @__PURE__ */ jsx("option", { value: size, children: size }, size);
              })
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      Button,
      {
        onClick: pageBackHandler,
        disabled: !backEnabled || disabled,
        minimal: true,
        className: "paging-arrow-left",
        icon: "chevron-left"
      }
    ),
    /* @__PURE__ */ jsx("div", { children: hideTotalPages ? page : total ? /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsx(
        PagingInput,
        {
          disabled,
          onBlur: pageInputBlurHandler,
          defaultPage: page,
          lastPage
        }
      ),
      "of ",
      lastPage
    ] }) : "No Rows" }),
    /* @__PURE__ */ jsx(
      Button,
      {
        style: { marginLeft: 5 },
        disabled: (controlled_hasNextPage === void 0 ? !forwardEnabled : !controlled_hasNextPage) || disabled,
        icon: "chevron-right",
        minimal: true,
        className: "paging-arrow-right",
        onClick: pageForwardHandler
      }
    )
  ] });
};
export default PagingTool;
